import { deleteReportEventsItem, getAllReportEventsItems } from 'src/api/report-events/offlineReportEventsApi';
import useApi from 'src/api/useApi';
import { useSWService } from 'src/api/sw/useSWService';

// CreateEvent returns 409 exclusively for a duplicate-event conflict (see
// TankersEventDatabaseRepository.CreateEvent: the pre-check row match and the
// DbUpdateException concurrent-insert catch are the only two Conflict throws
// reachable from CreateEvent — the delete-only "paired successor" conflict is
// not). If the SW's optimistic save reported a network error after the server
// had already processed the original request, a replayed createEventRequest
// hitting this conflict means the event IS already saved — treat it as done
// instead of leaving it queued to retry forever.
const isDuplicateCreateEventConflict = (response: any): boolean => {
    return Boolean(response?.isAxiosError) && response.response?.status === 409;
};

const useSyncOfflineReportEvents = () => {
    const { postApiData } = useApi();
    const { refreshRequestsContaining } = useSWService();

    const syncReportEvents = async (skipRefresh?: boolean | null): Promise<void> => {
        const reportEventsItems = await getAllReportEventsItems();
        reportEventsItems.sort((a, b) => a.lastUpdated - b.lastUpdated);

        for (let i = 0; i < reportEventsItems.length; i++) {
            const item = reportEventsItems[i];
            const request = item.deleteEventRequest ?? item.updateEventRequest ?? item.createEventRequest;

            if (request) {
                try {
                    const response = await postApiData({
                        fullUrl: request.url,
                        data: request.body,
                        skipRefreshCacheEndpoints: i < reportEventsItems.length - 1,
                        shouldReturnError: true,
                    });

                    if (request === item.createEventRequest && isDuplicateCreateEventConflict(response)) {
                        console.info('syncReportEvents: Event already saved on server, skipping.', item.eventId);
                        await deleteReportEventsItem(item.eventId);
                        continue;
                    }

                    if (response && !response?.isAxiosError) {
                        await deleteReportEventsItem(item.eventId);
                    }
                } catch (error) {
                    console.warn('Failed to sync report event item, will retry next cycle.', item.eventId, error);
                }
            }
        }

        if (!skipRefresh) {
            await refreshRequestsContaining(['GetEvents', 'GetDeltaEvents']);
        }
    };

    return {
        syncReportEvents,
    };
};

export default useSyncOfflineReportEvents;
